import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Lock, Tag, Award, CircleCheck, ArrowRight, Loader2 } from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api, ApiError, type CardSummary, type GraderLookupResp } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";

type Parsed = GraderLookupResp["parsed"];

export function Sell() {
  const { t } = useI18n();
  const { wallet, loading: authLoading, connect } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedCardId = searchParams.get("card_id");
  // Préremplissage depuis la grille BUY/SELL (TradeGridDialog) : grader et prix
  // sont de vraies valeurs de départ, mais `grade` reste un INDICE d'affichage
  // uniquement — la valeur publiée vient toujours de parsed.grade (vérification
  // PSA réelle), jamais de ce paramètre, pour ne jamais publier une note fausse
  // sur un cert réel.
  const preselectedGrader = searchParams.get("grader");
  const preselectedPrice = searchParams.get("price");
  const gradeHint = searchParams.get("grade");

  const [grader, setGrader] = useState(preselectedGrader === "Collectaura" ? "Collectaura" : "PSA");
  const [cert, setCert] = useState("");
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [graderSetKey, setGraderSetKey] = useState<string | null>(null);
  const [cachedExtension, setCachedExtension] = useState<string | null>(null);
  const [lookupStatus, setLookupStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [cardQuery, setCardQuery] = useState("");
  const [cardResults, setCardResults] = useState<CardSummary[]>([]);
  const [searchingCards, setSearchingCards] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const [price, setPrice] = useState(preselectedPrice ?? "");
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishedListingId, setPublishedListingId] = useState<number | null>(null);

  const onVerify = async () => {
    if (!cert.trim()) {
      setLookupStatus("error");
      setLookupError(t("sell.cert_missing") || "Enter a cert number.");
      return;
    }
    setLookupStatus("loading");
    setLookupError(null);
    try {
      const r = await api<GraderLookupResp>("/api/grader/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grader, cert_number: cert.trim() }),
      });
      setParsed(r.parsed);
      setGraderSetKey(r.grader_set_key ?? null);
      setCachedExtension(r.cached_extension ?? null);
      setLookupStatus("ok");
      const seed = r.suggested_query || preselectedCardId || "";
      if (seed) {
        setCardQuery(seed);
        await runCardSearch(seed, r.cached_extension ?? null, preselectedCardId);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setLookupStatus("error");
      setLookupError(msg);
      setParsed(null);
    }
  };

  const runCardSearch = useCallback(
    async (q: string, prioritizeExt: string | null = cachedExtension, autoSelectId: string | null = null) => {
      setSearchingCards(true);
      try {
        const r = await api<{ items: CardSummary[] }>(`/api/cards?search=${encodeURIComponent(q)}&limit=30`);
        const items = r.items || [];
        const sorted = items.slice().sort((a, b) => {
          const aMatch = prioritizeExt && a.id?.split("_")[1] === prioritizeExt ? 1 : 0;
          const bMatch = prioritizeExt && b.id?.split("_")[1] === prioritizeExt ? 1 : 0;
          return bMatch - aMatch;
        });
        setCardResults(sorted);
        if (autoSelectId && sorted.some((c) => c.id === autoSelectId)) {
          setSelectedCardId(autoSelectId);
        }
      } catch (e) {
        console.error("[sell] search cards:", e);
        setCardResults([]);
      } finally {
        setSearchingCards(false);
      }
    },
    [cachedExtension],
  );

  const onPublish = async () => {
    setPublishError(null);
    if (!selectedCardId || !parsed) {
      setPublishError(t("sell.missing") || "Select a card and verify the cert first.");
      return;
    }
    const priceNum = Number(price);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setPublishError(t("sell.bad_price") || "Invalid price.");
      return;
    }
    setPublishing(true);
    try {
      const r = await api<{ listing: { id: number } }>("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grader,
          cert_number: parsed.cert_number,
          card_id: selectedCardId,
          grade: parsed.grade,
          price_usdc: priceNum,
          grader_set_key: graderSetKey,
        }),
      });
      setPublishedListingId(r.listing.id);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setPublishError(msg);
    } finally {
      setPublishing(false);
    }
  };

  // Reset published state when user edits anything
  useEffect(() => {
    setPublishedListingId(null);
  }, [grader, cert, selectedCardId, price]);

  if (authLoading) {
    return (
      <div className="grid place-items-center py-16 text-muted-fg">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!wallet) {
    return (
      <Card className="mx-auto max-w-md">
        <CardBody className="grid place-items-center gap-3 p-8 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-warn/15 text-warn">
            <Lock className="h-5 w-5" />
          </div>
          <CardTitle>{t("sell.login_required.title") || "Connect to list a card"}</CardTitle>
          <CardDescription>
            {t("sell.login_required") || "You must sign in with MetaMask to publish a listing."}
          </CardDescription>
          <Button onClick={() => connect().catch((e) => alert(e.message))}>
            <Lock className="h-3.5 w-3.5" />
            Connect wallet
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (publishedListingId != null) {
    return (
      <Card className="mx-auto max-w-md">
        <CardBody className="grid place-items-center gap-3 p-8 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-success/15 text-success">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <CardTitle>{t("sell.success.title") || "Listing published!"}</CardTitle>
          <CardDescription>
            #{publishedListingId} · {selectedCardId} · {grader} {parsed?.grade}
          </CardDescription>
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            <Button onClick={() => navigate("/me")}>
              {t("sell.success.see_my") || "View my listings"} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" onClick={() => { setPublishedListingId(null); setCert(""); setParsed(null); setSelectedCardId(null); setPrice(""); }}>
              {t("sell.success.list_another") || "List another card"}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
          <Award className="h-3.5 w-3.5" />
          {t("sell.kicker") || "List for sale"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("sell.title") || "Sell a graded card"}
        </h1>
        <p className="mt-1 max-w-xl text-sm text-muted-fg">
          {t("sell.subtitle") ||
            "Verify your slab via the grader's online registry, match it to a catalog card, set your price."}
        </p>
        {gradeHint ? (
          <p className="mt-2 max-w-xl rounded-md border border-border bg-surface-2 px-3 py-2 text-xs text-muted-fg">
            Tu as sélectionné <span className="font-mono font-medium text-fg">{preselectedGrader || "PSA"} {gradeHint}</span> dans
            la grille — vérifie ton certificat ci-dessous pour confirmer la note réelle avant publication.
          </p>
        ) : null}
      </header>

      {/* Step 1 — Slab identity */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>
              <span className="mr-2 inline-grid h-6 w-6 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-fg">
                1
              </span>
              {t("sell.step1.title") || "Slab identity"}
            </CardTitle>
            <CardDescription>{t("sell.step1.help") || "Pull cert info from the grader (PSA live, others coming)."}</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[140px_1fr_auto] sm:items-end">
            <div>
              <label className="mb-1 block text-xs text-muted-fg">{t("card.ob.grader") || "Grader"}</label>
              <select
                value={grader}
                onChange={(e) => setGrader(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-surface px-2.5 text-sm"
              >
                <option value="PSA">PSA</option>
                <option value="Collectaura" disabled>Collectaura (soon)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-fg">{t("sell.step1.cert") || "Cert number"}</label>
              <Input
                placeholder="80000001"
                value={cert}
                onChange={(e) => setCert(e.target.value)}
                inputMode="numeric"
              />
            </div>
            <Button onClick={onVerify} loading={lookupStatus === "loading"} disabled={!cert.trim()}>
              <Search className="h-3.5 w-3.5" />
              {t("sell.step1.verify") || "Verify cert"}
            </Button>
          </div>
          {lookupStatus === "error" && lookupError ? (
            <p className="text-xs text-danger">{lookupError}</p>
          ) : null}
          {parsed ? <PsaInfo parsed={parsed} /> : null}
        </CardBody>
      </Card>

      {/* Step 2 — Catalog match */}
      <Card className={cn(!parsed && "opacity-50 pointer-events-none")}>
        <CardHeader>
          <div>
            <CardTitle>
              <span className="mr-2 inline-grid h-6 w-6 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-fg">
                2
              </span>
              {t("sell.step2.title") || "Match to catalog card"}
            </CardTitle>
            <CardDescription>
              {t("sell.step2.help") || "We pre-fill the search from the cert. Cards from the same set show up first."}
            </CardDescription>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <Input
              leftIcon={<Search />}
              value={cardQuery}
              onChange={(e) => setCardQuery(e.target.value)}
              placeholder={t("sell.step2.search_label") || "Card ID or name"}
              className="flex-1 min-w-[260px]"
            />
            <Button variant="secondary" onClick={() => runCardSearch(cardQuery)} disabled={!cardQuery.trim()}>
              {t("sell.step2.search_btn") || "Search"}
            </Button>
          </div>
          <div className="space-y-1.5">
            {searchingCards ? (
              <p className="text-xs text-muted-fg">{t("common.loading") || "Loading…"}</p>
            ) : cardResults.length === 0 ? (
              <p className="text-xs text-muted-fg">{t("sell.step2.no_results") || "No results yet."}</p>
            ) : (
              cardResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCardId(c.id)}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-xs transition-colors",
                    selectedCardId === c.id
                      ? "border-primary bg-primary/10 text-fg"
                      : "border-border hover:border-primary/50 hover:bg-surface-2",
                  )}
                >
                  <span className="flex flex-1 items-center gap-2 truncate">
                    <span className="font-mono text-primary">{c.id}</span>
                    <span className="truncate">{c.name ?? "—"}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-muted-fg">
                    {c.set_name ? <Badge tone="neutral" variant="soft">{c.set_name}</Badge> : null}
                    {c.rarity ? <Badge tone="info" variant="soft">{c.rarity}</Badge> : null}
                  </span>
                </button>
              ))
            )}
          </div>
          {selectedCardId ? (
            <p className="flex items-center gap-1 text-xs text-success">
              <CircleCheck className="h-3.5 w-3.5" /> {t("sell.step2.selected") || "Selected"}: <span className="font-mono">{selectedCardId}</span>
            </p>
          ) : null}
        </CardBody>
      </Card>

      {/* Step 3 — Price & publish */}
      <Card className={cn(!selectedCardId && "opacity-50 pointer-events-none")}>
        <CardHeader>
          <div>
            <CardTitle>
              <span className="mr-2 inline-grid h-6 w-6 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-fg">
                3
              </span>
              {t("sell.step3.title") || "Price & publish"}
            </CardTitle>
            <CardDescription>
              {t("sell.step3.help") || "Set your asking price in USDC. The listing goes live immediately."}
            </CardDescription>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="mb-1 block text-xs text-muted-fg">{t("sell.step3.price_label") || "Price (USDC)"}</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="12.50"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                leftIcon={<Tag />}
              />
            </div>
            <Button onClick={onPublish} loading={publishing} disabled={!price.trim() || !selectedCardId}>
              {t("sell.step3.publish") || "Publish listing"}
            </Button>
          </div>
          {publishError ? <p className="text-xs text-danger">{publishError}</p> : null}
        </CardBody>
      </Card>

      <p className="text-center text-xs text-muted-fg">
        <Link to="/marketplace" className="hover:underline">
          {t("sell.see_marketplace") || "Browse the marketplace →"}
        </Link>
      </p>
    </div>
  );
}

function PsaInfo({ parsed }: { parsed: Parsed }) {
  const rows: Array<[string, string | number | null | undefined]> = [
    ["Cert", parsed.cert_number],
    ["Year", parsed.year],
    ["Brand", parsed.brand],
    ["Category", parsed.category],
    ["Variety", parsed.variety],
    ["Card #", parsed.card_number],
    ["Subject", parsed.subject],
    ["Grade", parsed.grade],
    ["Pop @grade", `${parsed.population_at_grade ?? "?"} (higher: ${parsed.population_higher ?? "?"})`],
  ];
  return (
    <div className="grid gap-x-4 gap-y-1.5 rounded-md border border-border bg-surface-2/40 p-3 text-xs sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-muted-fg">{k}</span>
          <span className="truncate font-medium text-fg">{v ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}
